import { useMemo, useState } from "react";
import { Banknote, Check, History, Pencil, Plus, PowerOff, RotateCcw, Trash2, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { useConfirm } from "../../hooks/useConfirm";
import { getPosBankChanges } from "./posBankChanges";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

const makeId = () => globalThis.crypto?.randomUUID?.() || `bank-${Date.now()}`;
const cleanName = (value) => value.trim().replace(/\s+/g, " ");

const BankIconButton = ({ label, className = "", children, ...props }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={label}
        title={label}
        className={`transition-colors ${className}`}
        {...props}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent
      side="top"
      style={{ background: "#171717", color: "#ffffff", padding: 0 }}
      className="max-w-[240px] overflow-hidden border border-neutral-700 text-center shadow-lg"
    >
      <span style={{ display: "block", background: "#171717", color: "#ffffff", padding: "6px 12px" }}>
        {label}
      </span>
    </TooltipContent>
  </Tooltip>
);

export const PosBanksSection = ({ banks, originalBanks = [], onChange, onDeleteBank, loading = false }) => {
  const confirm = useConfirm();
  const [newName, setNewName] = useState("");
  const [newAlias, setNewAlias] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameAlias, setRenameAlias] = useState("");
  const originalById = useMemo(() => new Map(originalBanks.map((bank) => [bank.id, bank])), [originalBanks]);
  const changes = useMemo(() => getPosBankChanges(originalBanks, banks), [originalBanks, banks]);
  const activeBanks = banks.filter((bank) => bank.attiva !== false);
  const inactiveBanks = banks.filter((bank) => bank.attiva === false);

  const addBank = () => {
    const nome = cleanName(newName);
    const abbreviazione = cleanName(newAlias).toUpperCase();
    if (!nome || !abbreviazione) {
      toast.error("Inserisci nome e abbreviazione Telegram");
      return;
    }
    const existing = banks.find((bank) => cleanName(bank.nome).toLocaleLowerCase() === nome.toLocaleLowerCase());
    if (existing && existing.attiva !== false) {
      toast.error("Questa banca POS è già presente");
      return;
    }
    const aliasDuplicate = banks.some((bank) => bank.attiva !== false && (bank.abbreviazione || "").toUpperCase() === abbreviazione);
    if (aliasDuplicate) {
      toast.error("Questa abbreviazione Telegram è già utilizzata");
      return;
    }
    onChange(existing
      ? banks.map((bank) => bank.id === existing.id ? { ...bank, abbreviazione, attiva: true } : bank)
      : [...banks, { id: makeId(), nome, abbreviazione, attiva: true }]
    );
    setNewName("");
    setNewAlias("");
    toast.success(existing ? "Riattivazione aggiunta alla bozza" : "Banca aggiunta alla bozza");
  };

  const removeBank = async (bank) => {
    if (!originalById.has(bank.id)) {
      onChange(banks.filter((item) => item.id !== bank.id));
      return;
    }
    const lastActive = activeBanks.length === 1;
    const message = lastActive
      ? `Disattivando ${bank.nome} non resteranno banche attive. Dopo il salvataggio, i nuovi inserimenti torneranno al POS Generico. Lo storico resterà invariato.`
      : `Disattivare ${bank.nome}? Non sarà più richiesta nei nuovi inserimenti, ma tutti i movimenti storici resteranno invariati e visibili.`;
    const accepted = await confirm(message, {
      title: lastActive ? "Passare al POS Generico?" : "Disattivare banca POS?",
      confirmLabel: "Aggiungi alla bozza",
      confirmClass: "bg-red-600 hover:bg-red-700",
    });
    if (accepted) onChange(banks.map((item) => item.id === bank.id ? { ...item, attiva: false } : item));
  };

  const deleteBankPermanently = async (bank) => {
    if (changes.hasChanges) {
      toast.error("Prima salva o annulla le modifiche in bozza");
      return;
    }
    const lastActive = bank.attiva !== false && activeBanks.length === 1;
    const accepted = await confirm(
      lastActive
        ? `Eliminare definitivamente ${bank.nome}? È l'ultima banca attiva: i nuovi incassi torneranno al POS Generico. L'operazione è consentita solo perché non esistono transazioni associate.`
        : `Eliminare definitivamente ${bank.nome}? Il controllo non ha trovato incassi POS né uscite da estratto conto associate.`,
      {
        title: lastActive ? "Eliminare l'ultima banca?" : "Eliminare definitivamente la banca?",
        confirmLabel: "Elimina definitivamente",
        confirmClass: "bg-red-600 hover:bg-red-700",
      }
    );
    if (accepted) await onDeleteBank(bank);
  };

  const saveRename = async (bank) => {
    const nome = cleanName(renameValue);
    const abbreviazione = cleanName(renameAlias).toUpperCase();
    if (!abbreviazione) {
      toast.error("L'abbreviazione Telegram è obbligatoria");
      return;
    }
    if (!nome) {
      toast.error("Il nome della banca è obbligatorio");
      return;
    }
    if (nome === cleanName(bank.nome) && abbreviazione === (bank.abbreviazione || "").toUpperCase()) {
      setRenamingId(null);
      return;
    }
    const duplicate = banks.some((item) => item.id !== bank.id && cleanName(item.nome).toLocaleLowerCase() === nome.toLocaleLowerCase());
    if (duplicate) {
      toast.error("Esiste già una banca POS con questo nome");
      return;
    }
    const aliasDuplicate = banks.some((item) => item.id !== bank.id && item.attiva !== false && (item.abbreviazione || "").toUpperCase() === abbreviazione);
    if (aliasDuplicate) {
      toast.error("Questa abbreviazione Telegram è già utilizzata");
      return;
    }
    if (originalById.has(bank.id)) {
      const accepted = await confirm(
        `Aggiornare ${bank.nome} in ${nome} e usare l'abbreviazione ${abbreviazione}? Tutti i movimenti collegati tramite ID mostreranno subito il nuovo nome. Per un vero cambio banca devi disattivare la vecchia e crearne una nuova.`,
        { title: "Conferma rinomina banca", confirmLabel: "Aggiungi alla bozza", confirmClass: "bg-blue-600 hover:bg-blue-700" }
      );
      if (!accepted) return;
    }
    onChange(banks.map((item) => item.id === bank.id ? { ...item, nome, abbreviazione } : item));
    setRenamingId(null);
  };

  return (
    <TooltipProvider delayDuration={200}>
    <section className="space-y-4" data-testid="pos-banks-settings-section">
      <div>
        <Label className="text-foreground font-medium flex items-center gap-2"><Banknote className="h-4 w-4" /> Banche POS</Label>
        <p className="text-xs text-muted-foreground mt-1">Le modifiche restano in bozza fino al salvataggio del locale.</p>
        <p className="text-xs text-blue-600 mt-1" data-testid="pos-banks-history-notice">
          Alla prima banca potrai scegliere se lasciare i vecchi POS Generici o associarli esplicitamente.
        </p>
      </div>

      {loading && <p className="text-sm text-muted-foreground" data-testid="pos-banks-loading">Caricamento...</p>}
      {!loading && activeBanks.length === 0 && <p className="text-sm text-muted-foreground" data-testid="pos-banks-empty">Nessuna banca attiva: i nuovi inserimenti useranno un unico totale POS Generico.</p>}

      <div className="space-y-2">
        {activeBanks.map((bank) => {
          const previous = originalById.get(bank.id);
          const canDelete = Boolean(previous && bank.transazioni_associate === 0);
          const draftLabel = !previous ? "Da attivare" : previous.attiva === false ? "Da riattivare" : (previous.nome !== bank.nome || previous.abbreviazione !== bank.abbreviazione) ? "Modifica in bozza" : "Attiva";
          return (
            <div key={bank.id} className="flex items-center gap-2 border border-border rounded-md p-2" data-testid={`pos-bank-row-${bank.id}`}>
              <div className="min-w-0 flex-1">
                {renamingId === bank.id ? (
                  <div className="grid grid-cols-[1fr_92px] gap-2">
                    <Input value={renameValue} onChange={(event) => setRenameValue(event.target.value)} data-testid={`pos-bank-rename-input-${bank.id}`} autoFocus />
                    <Input value={renameAlias} onChange={(event) => setRenameAlias(event.target.value.toUpperCase())} placeholder="Alias" data-testid={`pos-bank-rename-alias-${bank.id}`} />
                  </div>
                ) : (
                  <div>
                    <p className="font-medium text-foreground truncate" data-testid={`pos-bank-name-${bank.id}`}>{bank.nome}</p>
                    <p className="text-xs text-muted-foreground" data-testid={`pos-bank-alias-${bank.id}`}>Telegram: {bank.abbreviazione || "da configurare"}</p>
                  </div>
                )}
                <span className={`text-xs ${draftLabel === "Attiva" ? "text-green-600" : "text-amber-600"}`}>{draftLabel}</span>
              </div>
              {renamingId === bank.id ? (
                <>
                  <BankIconButton label="Conferma modifica di nome e abbreviazione" className="hover:bg-green-100 hover:text-green-700" onClick={() => saveRename(bank)} data-testid={`pos-bank-rename-save-${bank.id}`}><Check className="h-4 w-4" /></BankIconButton>
                  <BankIconButton label="Annulla modifica" className="hover:bg-gray-100 hover:text-gray-700" onClick={() => setRenamingId(null)} data-testid={`pos-bank-rename-cancel-${bank.id}`}><X className="h-4 w-4" /></BankIconButton>
                </>
              ) : (
                <BankIconButton label={`Modifica nome e abbreviazione di ${bank.nome}`} className="hover:bg-blue-100 hover:text-blue-700" onClick={() => { setRenamingId(bank.id); setRenameValue(bank.nome); setRenameAlias(bank.abbreviazione || ""); }} data-testid={`pos-bank-rename-button-${bank.id}`}><Pencil className="h-4 w-4" /></BankIconButton>
              )}
              <BankIconButton label={previous ? `Disattiva ${bank.nome}: resta nello storico ma sparisce dai nuovi inserimenti` : `Rimuovi ${bank.nome} dalla bozza`} className={previous ? "hover:bg-amber-100 hover:text-amber-800" : "hover:bg-red-100 hover:text-red-700"} onClick={() => removeBank(bank)} data-testid={`pos-bank-remove-button-${bank.id}`}>
                {previous ? <PowerOff className="h-4 w-4 text-amber-600" /> : <Trash2 className="h-4 w-4 text-red-500" />}
              </BankIconButton>
              {canDelete && (
                <BankIconButton label={`Elimina definitivamente ${bank.nome}: disponibile perché non ha transazioni associate`} className="hover:bg-red-100 hover:text-red-700" onClick={() => deleteBankPermanently(bank)} data-testid={`pos-bank-delete-button-${bank.id}`}>
                  <Trash2 className="h-4 w-4 text-red-600" />
                </BankIconButton>
              )}
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_110px_auto] gap-2">
        <Input value={newName} onChange={(event) => setNewName(event.target.value)} placeholder="es. Banca di Roma" data-testid="pos-bank-new-name-input" />
        <Input value={newAlias} onChange={(event) => setNewAlias(event.target.value.toUpperCase())} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addBank(); } }} placeholder="Alias es. B" maxLength={12} data-testid="pos-bank-new-alias-input" />
        <Button type="button" variant="outline" onClick={addBank} disabled={!newName.trim() || !newAlias.trim()} data-testid="pos-bank-add-button"><Plus className="h-4 w-4" /> Bozza</Button>
      </div>

      {inactiveBanks.length > 0 && (
        <div className="border-t border-border pt-3 space-y-2" data-testid="inactive-pos-banks-section">
          <p className="text-xs font-semibold text-muted-foreground flex items-center gap-2"><History className="h-4 w-4" /> Banche inattive o da disattivare</p>
          {inactiveBanks.map((bank) => {
            const pending = originalById.get(bank.id)?.attiva !== false;
            return (
              <div key={bank.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`inactive-pos-bank-${bank.id}`}>
                <span className="text-muted-foreground">{bank.nome} · {bank.abbreviazione || "senza alias"} <small className="text-amber-600">{pending ? "Da disattivare" : "Inattiva"}</small></span>
                <div className="flex items-center gap-1">
                  <Button type="button" variant="ghost" size="sm" title={`Riattiva ${bank.nome} nei nuovi inserimenti`} aria-label={`Riattiva ${bank.nome} nei nuovi inserimenti`} className="hover:bg-green-100 hover:text-green-700" onClick={() => onChange(banks.map((item) => item.id === bank.id ? { ...item, attiva: true } : item))} data-testid={`pos-bank-reactivate-${bank.id}`}><RotateCcw className="h-4 w-4" /> Riattiva</Button>
                  {bank.transazioni_associate === 0 && originalById.has(bank.id) && (
                    <BankIconButton label={`Elimina definitivamente ${bank.nome}: disponibile perché non ha transazioni associate`} className="hover:bg-red-100 hover:text-red-700" onClick={() => deleteBankPermanently(bank)} data-testid={`pos-bank-delete-button-${bank.id}`}><Trash2 className="h-4 w-4 text-red-600" /></BankIconButton>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {changes.hasChanges && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="pos-bank-draft-summary">
          Modifiche non salvate: {changes.added.length + changes.reactivated.length} attivazioni, {changes.renamed.length} rinomine, {changes.deactivated.length} disattivazioni.
        </div>
      )}
    </section>
    </TooltipProvider>
  );
};